import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import './practitionerList.css';
import FormatUrl from "../../utils/UrlFormatter";

const PractitionerList = ({ clinicId, shouldRefetch }) => {
  const { t } = useTranslation();

  const [practitioners, setPractitioners] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [csrfToken, setCsrfToken] = useState('');
  const [selectedDiscipline, setSelectedDiscipline] = useState('');
  const [selectedFirstName, setSelectedFirstName] = useState('');
  const [selectedMiddleName, setSelectedMiddleName] = useState('');
  const [selectedLastName, setSelectedLastName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const practitionersPerPage = 6;
  const [activeTab, setActiveTab] = useState('practitioners');

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const csrfUrl = FormatUrl('/csrf-token/');
        const csrfResponse = await fetch(csrfUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!csrfResponse.ok) throw new Error('Failed to fetch CSRF token');
        const csrfData = await csrfResponse.json();
        setCsrfToken(csrfData.csrfToken);
      } catch (error) {
        console.error("Error fetching CSRF token:", error);
      }
    };

    fetchCsrfToken();
  }, []);

  useEffect(() => {
    const fetchPractitioners = async () => {
      try {
        const apiUrl = FormatUrl(`/get-doctors/?clinic_id=${clinicId}`);
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`Failed to fetch practitioners.`);
        const data = await response.json();
        setPractitioners(data.practitioners || []);
      } catch (error) {
        console.error("Error fetching practitioners:", error);
      }
    };

    const fetchClinicAdmins = async () => {
      try {
        const apiUrl = FormatUrl(`/get-clinic-admin-owner/?clinic_id=${clinicId}`);
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`Failed to fetch clinic admins/owners.`);
        const data = await response.json();
        setAdmins(data.admins || []);
      } catch (error) {
        console.error("Error fetching clinic admins/owners:", error);
      }
    };

    if (clinicId && csrfToken) {
      fetchPractitioners();
      fetchClinicAdmins();
    }
  }, [clinicId, csrfToken, shouldRefetch]);

  const handleDisciplineChange = (e) => {
    setSelectedDiscipline(e.target.value);
    setSelectedFirstName('');
    setSelectedMiddleName('');
    setSelectedLastName('');
    setCurrentPage(1);
  };

  const filteredPractitioners = practitioners.filter((p) => {
    return (
      (selectedDiscipline === '' || p.discipline === selectedDiscipline) &&
      (selectedFirstName === '' || p.firstName === selectedFirstName) &&
      (selectedMiddleName === '' || p.middleName === selectedMiddleName) &&
      (selectedLastName === '' || p.lastName === selectedLastName)
    );
  });

  const indexOfLast = currentPage * practitionersPerPage;
  const indexOfFirst = indexOfLast - practitionersPerPage;
  const currentPractitioners = filteredPractitioners.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredPractitioners.length / practitionersPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const uniqueDisciplines = [...new Set(practitioners.map(p => p.discipline))];
  const uniqueFirstNames = [...new Set(practitioners.filter(p => selectedDiscipline === '' || p.discipline === selectedDiscipline).map(p => p.firstName))];
  const uniqueMiddleNames = [...new Set(practitioners.filter(p => selectedDiscipline === '' || p.discipline === selectedDiscipline).map(p => p.middleName))];
  const uniqueLastNames = [...new Set(practitioners.filter(p => selectedDiscipline === '' || p.discipline === selectedDiscipline).map(p => p.lastName))];

  return (
    <div className="practitioner-list-custom">
      <h3 className="add-practitioner-title">{t("staff_list.practitioner_list")}</h3>

      <div className="tabs-custom">
        <button className={activeTab === 'practitioners' ? 'active' : ''} onClick={() => setActiveTab('practitioners')}>
          {t("staff_list.tab_practitioners")}
        </button>
        <button className={activeTab === 'admins' ? 'active' : ''} onClick={() => setActiveTab('admins')}>
          {t("staff_list.tab_admins")}
        </button>
      </div>

      {activeTab === 'practitioners' && (
        <>
          <div className="filters-custom">
            <select value={selectedDiscipline} onChange={handleDisciplineChange}>
              <option value="">{t("staff_list.all_disciplines")}</option>
              {uniqueDisciplines.map((d, i) => <option key={i} value={d}>{d}</option>)}
            </select>

            <div className="name-fields-custom">
              <select value={selectedFirstName} onChange={(e) => setSelectedFirstName(e.target.value)} disabled={!selectedDiscipline}>
                <option value="">{t("staff_list.all_first_names")}</option>
                {uniqueFirstNames.map((name, i) => <option key={i} value={name}>{name}</option>)}
              </select>

              <select value={selectedMiddleName} onChange={(e) => setSelectedMiddleName(e.target.value)} disabled={!selectedDiscipline}>
                <option value="">{t("staff_list.all_middle_names")}</option>
                {uniqueMiddleNames.map((name, i) => <option key={i} value={name}>{name}</option>)}
              </select>

              <select value={selectedLastName} onChange={(e) => setSelectedLastName(e.target.value)} disabled={!selectedDiscipline}>
                <option value="">{t("staff_list.all_last_names")}</option>
                {uniqueLastNames.map((name, i) => <option key={i} value={name}>{name}</option>)}
              </select>
            </div>
          </div>

          <div className="practitioner-grid-custom">
            {currentPractitioners.length > 0 ? (
              currentPractitioners.map((p) => (
                <div key={p.id} className={`practitioner-card-custom ${p.is_registered ? 'registered-card' : 'unregistered-card'}`}>
                  <div className="card-content-custom">
                    <h4>{`${p.firstName} ${p.middleName ? p.middleName + ' ' : ''}${p.lastName}`}</h4>
                    <p><strong>ID:</strong> {p.id}</p>
                    {p.displayName && <p><strong>{t("staff_list.display_name")}:</strong> {p.displayName}</p>}
                    {p.gender && <p><strong>{t("staff_list.gender")}:</strong> {p.gender === "M" ? t("staff_add.gender_male") : p.gender === "F" ? t("staff_add.gender_female") : p.gender}</p>}
                    <p><strong>{t("staff_list.email")}:</strong> {p.email}</p>
                    <p><strong>{t("staff_list.phone")}:</strong> {p.phone}</p>
                    <p><strong>{t("staff_list.discipline")}:</strong> {p.discipline}</p>
                    <p><strong>{t("staff_list.licensed_practitioner")}:</strong> {p.is_registered ? t("staff_list.yes") : t("staff_list.no")}</p>
                    {p.is_registered && <p><strong>{t("staff_list.license_number")}:</strong> {p.licenseNumber}</p>}
                    {p.skills && (
                      <div className="skill-section-custom">
                        <p><strong>{t("staff_list.skills")}:</strong></p>
                        <div className="skill-tag-container">
                          {p.skills.split(',').map((skill, index) => (
                            <span key={index} className="skill-tag-pill">{skill.trim()}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-practitioner-card-custom">
                <h4>{t("staff_list.no_practitioners")}</h4>
                <p>{t("staff_list.add_practitioners_message")}</p>
              </div>
            )}
          </div>

          <div className="pagination-custom">
            <button onClick={handlePreviousPage} disabled={currentPage === 1}>{t("staff_list.previous")}</button>
            <span>{t("staff_list.page")} {currentPage} {t("staff_list.of")} {totalPages}</span>
            <button onClick={handleNextPage} disabled={currentPage === totalPages}>{t("staff_list.next")}</button>
          </div>
        </>
      )}

      {activeTab === 'admins' && (
        <div className="clinic-admins-section">
          {admins.length > 0 ? (
            <div className="admin-grid-custom">
              {admins.map((admin, index) => (
                <div
                  key={index}
                  className={`admin-card-custom ${admin.role === 'CO' ? 'owner-card' : 'admin-card'}`}
                >
                  <h4>{`${admin.first_name} ${admin.last_name}`}</h4>
                  <p><strong>{t("staff_list.email")}:</strong> {admin.email}</p>
                  <p><strong>{t("staff_list.phone")}:</strong> {admin.phone}</p>
                  <p><strong>{t("staff_list.role")}:</strong> {admin.role === 'CO' ? t("staff_list.clinic_owner") : t("staff_list.clinic_admin")}</p>
                </div>
              ))}
            </div>
          ) : (
            <p>{t("staff_list.no_admins_found")}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PractitionerList;
